import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createRoot } from 'react-dom/client';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Home from './pages/Home';
import Leads from './pages/Leads';
import Opportunities from './pages/Opportunities';
import Pipeline from './pages/Pipeline';
import Customers from './pages/Customers';
import Estimates from './pages/Estimates';
import Proposals from './pages/Proposals';
import ContractsPage from './pages/Contracts';
import Employees from './pages/Employees';
import Recruitment from './pages/Recruitment';
import Leave from './pages/Leave';
import Resources from './pages/Resources';
import ContractAllocations from './pages/ContractAllocations';
import ContractEditor from './pages/ContractEditor';
import LeaveESS from './pages/LeaveESS';
import LeaveHCM from './pages/LeaveHCM';
import AdminPage from './pages/Admin';
import EmailSettings from './pages/EmailSettings';
import Integrations from './pages/Integrations';
import WorkforceLive from './pages/WorkforceLive';
import SalesDashboard from './pages/SalesDashboard';
import HRDashboard from './pages/HRDashboard';
import FinanceDashboard from './pages/FinanceDashboard';
import Projects from './pages/Projects';
import Timesheets from './pages/Timesheets';
import Billing from './pages/Billing';
import Revenue from './pages/Revenue';
import Vendors from './pages/Vendors';
import Procurement from './pages/Procurement';
import Finance from './pages/Finance';
import Salary from './pages/Salary';
import Reimbursements from './pages/Reimbursements';
import ESS from './pages/ESS';
import Metadata from './pages/Metadata';
import OrgStructure from './pages/OrgStructure';
import GroupTags from './pages/GroupTags';
import Offices from './pages/Offices';
import Grades from './pages/Grades';
import SalarySchemas from './pages/SalarySchemas';
import SalaryComponentMasters from './pages/SalaryComponentMasters';
import PricingSchemas from './pages/PricingSchemas';
import PricingComponentMasters from './pages/PricingComponentMasters';
import AccountingGuide from './pages/AccountingGuide';
import LetterBuilder from './pages/LetterBuilder';
import Reminders from './pages/Reminders';
import WorkflowEngine from '../plugins/workflow-engine/client/WorkflowEngine';

/* ================================================================
   Global App Context
   ================================================================ */
export const AppContext = createContext(null);

export function useApp() {
  return useContext(AppContext);
}

/* ================================================================
   API Helper
   ================================================================ */
/* ─── Token refresh plumbing ───
   On 401, try /api/auth/refresh with the httpOnly cookie (or stored refreshToken),
   save the new access token, and retry the original request once.
   Only falls through to /login.html if the refresh itself fails. */
let _refreshInFlight = null;

async function tryRefresh() {
  // De-dupe concurrent refresh attempts
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const storedRefresh = localStorage.getItem('bizsuite_refresh');
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(storedRefresh ? { refreshToken: storedRefresh } : {}),
      });
      if (!res.ok) return null;
      const d = await res.json();
      if (d.token) localStorage.setItem('bizsuite_token', d.token);
      if (d.refreshToken) localStorage.setItem('bizsuite_refresh', d.refreshToken);
      return d.token || null;
    } catch (_) {
      return null;
    } finally {
      setTimeout(() => { _refreshInFlight = null; }, 0);
    }
  })();
  return _refreshInFlight;
}

export async function apiFetch(url, options = {}) {
  function buildHeaders(token) {
    const h = options.skipContentType ? { ...(options.headers || {}) } : { 'Content-Type': 'application/json', ...(options.headers || {}) };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }

  const { skipContentType, ...fetchOptions } = options;

  // Attempt 1 — current token
  let token = localStorage.getItem('bizsuite_token');
  let res = await fetch(url, { ...fetchOptions, headers: buildHeaders(token), credentials: 'include' });

  if (res.status !== 401) return res;

  // Don't try to refresh the refresh endpoint itself (avoids infinite loops)
  if (url.includes('/api/auth/refresh') || url.includes('/api/auth/login')) {
    return res;
  }

  // Attempt 2 — silently refresh, then retry once
  const newToken = await tryRefresh();
  if (!newToken) {
    localStorage.removeItem('bizsuite_token');
    localStorage.removeItem('bizsuite_refresh');
    window.location.href = '/login.html';
    throw new Error('Unauthorized');
  }

  const res2 = await fetch(url, { ...fetchOptions, headers: buildHeaders(newToken), credentials: 'include' });

  if (res2.status === 401) {
    // Refresh succeeded but the retried request still 401'd — genuine auth failure.
    localStorage.removeItem('bizsuite_token');
    localStorage.removeItem('bizsuite_refresh');
    window.location.href = '/login.html';
    throw new Error('Unauthorized');
  }

  return res2;
}

/* ================================================================
   Route definitions
   ================================================================ */
/* Neon palette matching sidebar icon colors */
const NEON = {
  cyan: '#00F0FF', magenta: '#FF00E5', lime: '#39FF14', orange: '#FF6B00',
  violet: '#BF5FFF', pink: '#FF2D7B', teal: '#00E5A0', blue: '#3B8FF4',
  yellow: '#FFE500', coral: '#FF4F4F', mint: '#00FFC8', gold: '#FFB300',
  sky: '#00BFFF', rose: '#FF6B9D',
};

const ROUTES = {
  // Nouveau-mode launchpad. Hidden from Classic-mode sidebar; reachable via
  // the header Classic↔Nouveau toggle or by setting nav_mode = 'nouveau'.
  '/home': { component: Home, title: 'Home', neon: NEON.cyan },
  '/dashboard': { component: Dashboard, title: 'Dashboard', neon: NEON.magenta },
  '/crm/leads': { component: Leads, title: 'Leads', neon: NEON.lime },
  '/crm/opportunities': { component: Opportunities, title: 'Opportunities', neon: NEON.lime },
  '/crm/pipeline': { component: Pipeline, title: 'Pipeline Board', neon: NEON.lime },
  '/customers': { component: Customers, title: 'Customers', neon: NEON.orange },
  '/estimates/rate-cards': { component: Estimates, title: 'Rate Cards', neon: NEON.violet },
  '/estimates': { component: Estimates, title: 'Estimates', neon: NEON.violet },
  '/proposals': { component: Proposals, title: 'Proposals', neon: NEON.violet },
  '/contracts': { component: ContractsPage, title: 'Contracts', neon: NEON.violet },
  '/contracts/new': { component: ContractEditor, title: 'New Contract', neon: NEON.violet },
  '/contracts/:id/edit': { component: ContractEditor, title: 'Edit Contract', neon: NEON.violet },
  '/contracts/:id/view': { component: ContractEditor, title: 'View Contract', neon: NEON.violet },
  '/employees': { component: Employees, title: 'Employees', neon: NEON.pink },
  '/employees/live': { component: WorkforceLive, title: 'Workforce Live View', neon: NEON.pink },
  '/letter-builder': { component: LetterBuilder, title: 'Letter Template Builder', neon: NEON.pink },
  '/recruitment': { component: Recruitment, title: 'Recruitment', neon: NEON.pink },
  '/leave': { component: Leave, title: 'Leave Management', neon: NEON.pink },
  '/resources': { component: ContractAllocations, title: 'Contract Allocation', neon: NEON.teal },
  '/resources/legacy': { component: Resources, title: 'Legacy Allocations', neon: NEON.teal },
  '/resources/bench': { component: Resources, title: 'Bench Pool', neon: NEON.teal },
  '/resources/utilization': { component: Resources, title: 'Utilization', neon: NEON.teal },
  '/admin': { component: AdminPage, title: 'Administration', neon: NEON.rose },
  // Old /admin/email URL kept as a deep-link alias — opens Integrations on the Email tab.
  '/admin/email': { component: Integrations, title: 'Integrations', neon: NEON.rose, props: { initialTab: 'email' } },
  '/admin/integrations': { component: Integrations, title: 'Integrations', neon: NEON.rose },
  '/reminders': { component: Reminders, title: 'Reminders', neon: NEON.cyan },
  '/dashboard/sales': { component: SalesDashboard, title: 'Sales Dashboard', neon: NEON.magenta },
  '/dashboard/hr': { component: HRDashboard, title: 'HR Dashboard', neon: NEON.magenta },
  '/dashboard/finance': { component: FinanceDashboard, title: 'Finance Dashboard', neon: NEON.magenta },
  '/projects': { component: Projects, title: 'Projects', neon: NEON.blue },
  '/timesheets': { component: Timesheets, title: 'Timesheets', neon: NEON.blue },
  '/billing': { component: Billing, title: 'Billing & Invoices', neon: NEON.yellow },
  '/revenue': { component: Revenue, title: 'Revenue & Receivables', neon: NEON.yellow },
  '/vendors': { component: Vendors, title: 'Vendors', neon: NEON.coral },
  '/procurement': { component: Procurement, title: 'Procurement', neon: NEON.coral },
  '/finance': { component: Finance, title: 'Finance & Accounting', neon: NEON.mint },
  '/accounting-guide': { component: AccountingGuide, title: 'Accounting Guide', neon: NEON.mint },
  '/salary': { component: Salary, title: 'Salary & Payroll', neon: NEON.gold },
  '/reimbursements': { component: Reimbursements, title: 'Reimbursements', neon: NEON.gold },
  '/ess': { component: ESS, title: 'Self Service Portal', neon: NEON.cyan },
  '/ess/leave': { component: LeaveESS, title: 'Leave · Self Service', neon: NEON.cyan },
  '/hcm/leave-config': { component: LeaveHCM, title: 'HCM · Leave Configurator', neon: NEON.pink },
  '/offices': { component: Offices, title: 'Offices', neon: NEON.rose },
  '/metadata': { component: Metadata, title: 'Metadata Masters', neon: NEON.rose },
  '/org-structure': { component: OrgStructure, title: 'Organization Structure', neon: NEON.pink },
  '/group-tags': { component: GroupTags, title: 'Group Tags', neon: NEON.pink },
  '/grades': { component: Grades, title: 'Employee Grades', neon: NEON.pink },
  '/salary-schemas': { component: SalarySchemas, title: 'Salary Schemas', neon: NEON.pink },
  '/salary-schemas/component-masters':     { component: SalaryComponentMasters, title: 'Salary Component Masters', neon: NEON.pink },
  '/salary-schemas/:id/component-masters': { component: SalaryComponentMasters, title: 'Salary Component Masters', neon: NEON.pink },
  '/pricing-schemas': { component: PricingSchemas, title: 'Pricing Schemas', neon: NEON.rose },
  '/pricing-schemas/component-masters':     { component: PricingComponentMasters, title: 'Pricing Component Masters', neon: NEON.rose },
  '/pricing-schemas/:id/component-masters': { component: PricingComponentMasters, title: 'Pricing Component Masters', neon: NEON.rose },
  '/workflow': { component: WorkflowEngine, title: 'Workflow Engine', neon: NEON.cyan },
  '/workflow/forms': { component: WorkflowEngine, title: 'Form Builder', neon: NEON.cyan },
  '/workflow/designer': { component: WorkflowEngine, title: 'Workflow Designer', neon: NEON.cyan },
  '/workflow/instances': { component: WorkflowEngine, title: 'Workflow Instances', neon: NEON.cyan },
};

/* ================================================================
   Simple Hash Router
   ================================================================ */
function getRoute() {
  // Default landing is the user's My Portal (Self Service → Leave / WFH).
  // Was /dashboard previously — switched per request so each user lands on
  // their personal view first, not a global metric page.
  const hash = window.location.hash.replace('#', '') || '/ess/leave';
  return hash;
}

function navigate(path) {
  window.location.hash = '#' + path;
}

/* ================================================================
   Notification helpers
   ================================================================ */
function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/* ================================================================
   Main BizSuiteApp
   ================================================================ */
function BizSuiteApp() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [route, setRoute] = useState(getRoute());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Fetch user context on mount */
  useEffect(() => {
    let cancelled = false;

    async function fetchUser() {
      try {
        const res = await apiFetch('/api/auth/me');
        if (!res.ok) throw new Error('Unauthorized');
        const data = await res.json();
        if (!cancelled) {
          const u = data.user || data;
          setCurrentUser(u);
          setCurrentTenant(data.tenant || null);
          setLoading(false);
          // Nouveau users land on /home by default. We only redirect when the
          // hash is empty / the legacy default ('/ess/leave') so users who
          // deep-linked to a specific page still land where they intended.
          if (u && u.navMode === 'nouveau') {
            const hash = window.location.hash.replace('#', '');
            if (!hash || hash === '/ess/leave') {
              window.location.hash = '#/home';
              setRoute('/home');
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          window.location.href = '/login.html';
        }
      }
    }

    fetchUser();
    return () => { cancelled = true; };
  }, []);

  /* Hash change listener */
  useEffect(() => {
    function handleHash() {
      setRoute(getRoute());
    }
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  /* Fetch recent notifications */
  useEffect(() => {
    if (!currentUser) return;

    async function fetchNotifications() {
      try {
        const res = await apiFetch('/api/notifications?limit=5');
        if (res.ok) {
          const data = await res.json();
          setNotifications((data.notifications || []).slice(0, 5).map(n => ({
            id: n.id,
            message: n.title || n.body || 'Notification',
            time: formatTimeAgo(n.created_at),
            read: n.read === true || n.read === 1
          })));
        } else {
          const fallbackRes = await apiFetch('/api/crm/activities?limit=5&sort=-created_at');
          if (fallbackRes.ok) {
            const fbData = await fallbackRes.json();
            setNotifications((fbData.data || fbData.activities || []).slice(0, 5).map(a => ({
              id: a.id,
              message: a.subject || a.description || 'Activity update',
              time: formatTimeAgo(a.created_at),
              read: false
            })));
          }
        }
      } catch (e) {
        /* notifications are non-critical */
      }
    }

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, []);

  const handleLogout = useCallback(async () => {
    // Send the refresh token in the body so the server can revoke it — the
    // refresh cookie is path-scoped to /api/auth/refresh and won't auto-attach
    // to /logout, so without this the server has nothing to revoke.
    const refreshToken = localStorage.getItem('bizsuite_refresh');
    try {
      await apiFetch('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify(refreshToken ? { refreshToken } : {}),
      });
    } catch (e) { /* ignore */ }
    // Clear BOTH tokens — leaving the refresh token behind would let the next
    // visit silently re-auth via /api/auth/refresh and skip the login page.
    localStorage.removeItem('bizsuite_token');
    localStorage.removeItem('bizsuite_refresh');
    window.location.href = '/login.html';
  }, []);

  const markNotificationRead = useCallback((id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    apiFetch(`/api/notifications/${id}/read`, { method: 'PUT' }).catch(() => {});
  }, []);

  /* Loading state */
  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
        <span>Loading BizSuite...</span>
      </div>
    );
  }

  /* Resolve current page component — direct lookup first, then pattern match
   * for routes containing ":id" placeholders (e.g. /contracts/:id/edit). */
  let routeDef = ROUTES[route];
  if (!routeDef) {
    const parts = route.split('/').filter(Boolean);
    for (const [pattern, def] of Object.entries(ROUTES)) {
      const pparts = pattern.split('/').filter(Boolean);
      if (pparts.length !== parts.length) continue;
      const match = pparts.every((seg, i) => seg.startsWith(':') || seg === parts[i]);
      if (match) { routeDef = def; break; }
    }
  }
  let PageComponent = Dashboard;
  let pageTitle = 'Dashboard';

  let pageNeon = NEON.magenta; // default
  if (routeDef) {
    PageComponent = routeDef.component;
    pageTitle = routeDef.title;
    pageNeon = routeDef.neon || NEON.magenta;
  }
  // Personalised topbar title for My Portal — swap the static "Leave · Self
  // Service" with "Welcome <FirstName>" when the user is on /ess/leave. We
  // pull the first name from the linked employee row (auth/me); fall back to
  // the first token of fullName, then to "there" if neither is available.
  if (route === '/ess/leave' && currentUser) {
    const fn = currentUser.firstName || (currentUser.fullName || '').split(' ')[0] || 'there';
    pageTitle = `Welcome ${fn}`;
  }

  /* Admin access is enforced server-side via requireTenantAdmin middleware */

  const contextValue = {
    currentUser,
    currentTenant,
    notifications,
    markNotificationRead,
    navigate: handleNavigate,
    logout: handleLogout,
    route
  };

  /* Persist a Classic↔Nouveau toggle. Optimistic-updates the local user state
   * so the UI reacts instantly, then PUTs to /api/auth/me/preferences to make
   * it stick. If switching to Nouveau and the user is sitting on a route that
   * lives under the sidebar (i.e. anything but /home), we route them to the
   * launchpad so they actually see the new mode. Switching back to Classic
   * returns them to /ess/leave (the existing default landing). */
  async function handleNavModeChange(mode) {
    if (mode !== 'classic' && mode !== 'nouveau') return;
    setCurrentUser(u => u ? { ...u, navMode: mode } : u);
    try {
      await apiFetch('/api/auth/me/preferences', {
        method: 'PUT',
        body: JSON.stringify({ navMode: mode }),
      });
    } catch (e) { /* non-fatal — local state still updated */ }
    if (mode === 'nouveau' && route !== '/home') handleNavigate('/home');
    if (mode === 'classic' && route === '/home') handleNavigate('/ess/leave');
  }

  return (
    <AppContext.Provider value={contextValue}>
      <Layout
        currentUser={currentUser}
        currentRoute={route}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        notifications={notifications}
        onMarkRead={markNotificationRead}
        pageNeon={pageNeon}
        navMode={currentUser?.navMode || 'classic'}
        onNavModeChange={handleNavModeChange}
      >
        <div className="page-header">
          <h1>{pageTitle}</h1>
        </div>
        {/* Spread routeDef.props so deep-link aliases (e.g. /admin/email →
            Integrations with initialTab='email') can preselect a child tab. */}
        <PageComponent {...(routeDef && routeDef.props ? routeDef.props : {})} />
      </Layout>
    </AppContext.Provider>
  );
}

/* ================================================================
   Mount
   ================================================================ */
const container = document.getElementById('app');
if (container) {
  const root = createRoot(container);
  root.render(<BizSuiteApp />);
}
